/**
 * Controls horizontal accessory product list navigation and Learn More dialog.
 */
if (!customElements.get('accessory-product-list')) {
  class AccessoryProductList extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.accessory-product-list__track');
      this.previousButton = this.querySelector('.accessory-product-list__button--previous');
      this.nextButton = this.querySelector('.accessory-product-list__button--next');

      this.handleLearnMoreClick = (event) => this.openProductDialog(event);
      this.addEventListener('click', this.handleLearnMoreClick);

      if (!this.track || !this.previousButton || !this.nextButton) {
        return;
      }

      this.handlePreviousClick = () => this.scrollByCard(-1);
      this.handleNextClick = () => this.scrollByCard(1);
      this.handleTrackScroll = () => this.updateNavigation();
      this.handleResize = () => this.updateNavigation();

      this.previousButton.addEventListener('click', this.handlePreviousClick);
      this.nextButton.addEventListener('click', this.handleNextClick);
      this.track.addEventListener('scroll', this.handleTrackScroll, { passive: true });

      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.track);

      this.updateNavigation();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleLearnMoreClick);
      this.previousButton?.removeEventListener('click', this.handlePreviousClick);
      this.nextButton?.removeEventListener('click', this.handleNextClick);
      this.track?.removeEventListener('scroll', this.handleTrackScroll);
      this.resizeObserver?.disconnect();
    }

    /**
     * Scrolls the list by one visible card.
     * @param {number} direction - -1 for previous or 1 for next.
     */
    scrollByCard(direction) {
      const card = this.track.querySelector('.accessory-product-list__card');

      if (!card) {
        return;
      }

      const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const distance = (card.getBoundingClientRect().width + gap) * direction;

      this.track.scrollBy({
        left: distance,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    }

    /**
     * Sets disabled states from the native scroll position.
     */
    updateNavigation() {
      const maximumScroll = this.track.scrollWidth - this.track.clientWidth;
      const scrollTolerance = 1;

      this.previousButton.disabled = this.track.scrollLeft <= scrollTolerance;
      this.nextButton.disabled = this.track.scrollLeft >= maximumScroll - scrollTolerance;
    }

    /**
     * Fetches product markup and opens the shared product info dialog.
     * @param {MouseEvent} event
     */
    async openProductDialog(event) {
      const learnMoreButton = event.target.closest('.accessory-product-list__learn-more');

      if (!learnMoreButton) {
        return;
      }

      const card = learnMoreButton.closest('.accessory-product-list__card');
      const content = card?.querySelector('.accessory-product-list__content');
      const handle = card?.dataset.handle;
      const variantId = card?.dataset.variantId;

      if (!card || !content || !handle) {
        return;
      }

      const dialog = document.querySelector('#product-info-dialog');
      const dialogInfo = dialog?.querySelector('.dialog-body-box');

      if (!dialog || !dialogInfo) {
        return;
      }

      content.classList.add('is-loading');

      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = variantId
          ? `${root}products/${handle}?variant=${variantId}`
          : `${root}products/${handle}`;
        const response = await fetch(url, { priority: 'low' });
        const tempDiv = new DOMParser().parseFromString(await response.text(), 'text/html');
        const productInfoElm = tempDiv.querySelector('#main .shopify-section--main-product');

        if (!productInfoElm) {
          return;
        }

        const addToCartBarWrapper = productInfoElm.querySelector('.add-to-cart-bar-wrapper');
        const quickBuyContent = productInfoElm.querySelector('#quickBuyContent');

        if (addToCartBarWrapper) {
          addToCartBarWrapper.parentNode.removeChild(addToCartBarWrapper);
        }

        if (quickBuyContent) {
          productInfoElm.removeChild(quickBuyContent);
        }

        dialogInfo.innerHTML = productInfoElm.outerHTML;
        dialog.setAttribute('data-layout', 'overview');
        dialog.setAttribute('open', '');
      } finally {
        content.classList.remove('is-loading');
      }
    }
  }

  customElements.define('accessory-product-list', AccessoryProductList);
}
